/**
 * Color utility functions for generating light/dark variants
 * Similar to Material-UI's color system (50, 100, 200, 300, 400, 500, 600, 700, 800, 900)
 */

/**
 * Convert hex to RGB
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Convert RGB to hex
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Lighten a color by a percentage
 */
export const lighten = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const { r, g, b } = rgb;
  const newR = Math.min(255, Math.round(r + (255 - r) * amount));
  const newG = Math.min(255, Math.round(g + (255 - g) * amount));
  const newB = Math.min(255, Math.round(b + (255 - b) * amount));
  
  return rgbToHex(newR, newG, newB);
};

/**
 * Darken a color by a percentage
 */
export const darken = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const { r, g, b } = rgb;
  const newR = Math.max(0, Math.round(r * (1 - amount)));
  const newG = Math.max(0, Math.round(g * (1 - amount)));
  const newB = Math.max(0, Math.round(b * (1 - amount)));
  
  return rgbToHex(newR, newG, newB);
};

/**
 * Add alpha (opacity) to a hex color
 */
export const alpha = (color: string, opacity: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
};

/**
 * Generate Material-UI style color palette from a base color
 * Returns an object with shades: 50, 100, 200, 300, 400, 500, 600, 700, 800, 900
 */
export const generateColorPalette = (baseColor: string) => {
  return {
    50: lighten(baseColor, 0.9),   // Very light
    100: lighten(baseColor, 0.8),  // Light
    200: lighten(baseColor, 0.6),  // Light
    300: lighten(baseColor, 0.4),  // Light
    400: lighten(baseColor, 0.2),  // Light
    500: baseColor,                // Base color
    600: darken(baseColor, 0.1),   // Dark
    700: darken(baseColor, 0.2),   // Dark
    800: darken(baseColor, 0.3),   // Dark
    900: darken(baseColor, 0.4),   // Very dark
  };
};

/**
 * Get a specific shade of a color (50-900)
 */
export const getColorShade = (baseColor: string, shade: 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900): string => {
  const palette = generateColorPalette(baseColor);
  return palette[shade];
};

/**
 * Quick access functions for common use cases
 */
export const getLight = (color: string) => getColorShade(color, 200);
export const getLighter = (color: string) => getColorShade(color, 100);
export const getLightest = (color: string) => getColorShade(color, 50);
export const getDark = (color: string) => getColorShade(color, 700);
export const getDarker = (color: string) => getColorShade(color, 800);
export const getDarkest = (color: string) => getColorShade(color, 900);

/**
 * Generate background and text color combinations for status badges
 */
export const generateStatusColors = (baseColor: string) => {
  return {
    bg: getColorShade(baseColor, 100),      // Light background
    color: getColorShade(baseColor, 800),   // Dark text
    marker: baseColor,                      // Original color for markers
    light: getColorShade(baseColor, 50),    // Very light variant
    dark: getColorShade(baseColor, 700),    // Dark variant
  };
};
